/**
 * 相机系统纯数学（P0-3a）
 *
 * 刻意与 React/three 场景图解耦：输入全是数据模型，输出是 three 的数学对象，
 * 这样注视点解析、朝向反解、网格吸附都能在 node 环境穷举单测——视口里
 * "镜头朝向不对"这种问题必须在这里被数学证明排除，而不是留给浏览器目视。
 *
 * 朝向策略（计划书 §3.2 已定）：相机**不存 rotation**，由 position+target 反解，
 * 竞品验证过的极简设计；这里补上竞品没做防的两类退化情形（同点、平行 up）。
 */
import { Matrix4, Quaternion, Vector3 } from 'three';

import {
  findObject,
  suggestTargetY,
  type DirectorCamera,
  type DirectorProject,
  type Vec3,
} from '../../model/director';

/** 相机 up 向量（glTF/three 约定 Y-up），不做可配置项——导演台不是 DCC 软件 */
export const DEFAULT_UP: Vec3 = [0, 1, 0];
/** 位置与注视点距离小于该值视为退化（无法定义朝向），不做任何"尽力而为" */
export const MIN_CAMERA_DISTANCE = 1e-4;
/** 前进方向与 up 夹角余弦超过该值视为退化，需换备用 up */
export const UP_PARALLEL_EPS = 0.999;

/**
 * 解析机位的实际注视点。
 * - point：直接用存储值（用户在面板里定的世界坐标）
 * - follow：跟随角色当前位置 + 按角色实际身高算的头高（蓝图 §7-6，
 *   修竞品 target.y 写死导致跪姿注视点在身体中部的问题）
 * 角色缺失或 followId 悬空时返回 null——调用方必须处理而不是硬算。
 */
export function resolveTargetPoint(
  project: DirectorProject,
  camera: DirectorCamera,
): Vec3 | null {
  if (camera.target.mode === 'point') {
    return camera.target.point;
  }
  if (camera.target.followId === null) {
    return null;
  }
  const obj = findObject(project, camera.target.followId);
  if (obj === undefined) {
    return null;
  }
  const [x, y, z] = obj.transform.position;
  return [x, y + suggestTargetY(project, camera), z];
}

/**
 * 由 position/target 反解相机朝向四元数（three 约定：相机看向自身 -Z）。
 * 两类退化情形显式处理，绝不产出 NaN 四元数——NaN 进场景图的表现是
 * 模型"闪没"，比报错难查十倍：
 *  - 位置与注视点重合（距离 < MIN_CAMERA_DISTANCE）→ null
 *  - 视线与 up 近平行（正俯视/正仰视）→ 换备用 up [0,0,-1]（保持"屏幕上方=世界 -Z"
 *    的直觉，与主流 DCC 正交视图行为一致）
 */
export function computeLookAtQuaternion(
  position: Vec3,
  target: Vec3,
): Quaternion | null {
  const eye = new Vector3(position[0], position[1], position[2]);
  const look = new Vector3(target[0], target[1], target[2]);
  if (eye.distanceToSquared(look) < MIN_CAMERA_DISTANCE * MIN_CAMERA_DISTANCE) {
    return null;
  }
  const forward = look.clone().sub(eye).normalize();
  let up = new Vector3(DEFAULT_UP[0], DEFAULT_UP[1], DEFAULT_UP[2]);
  if (Math.abs(forward.dot(up)) > UP_PARALLEL_EPS) {
    up = new Vector3(0, 0, -1);
  }
  const m = new Matrix4().lookAt(eye, look, up);
  return new Quaternion().setFromRotationMatrix(m);
}

/**
 * 网格吸附（scene.gridSnap 开启时对位移/坐标套用）。
 * step<=0 或非有限值时原样返回——吸附关闭不等于把坐标改成 0。
 * 结果按 6 位小数收敛，防 0.1*3 类浮点尾巴进入工程 JSON。
 */
export function snapToGrid(value: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) {
    return value;
  }
  const snapped = Math.round(value / step) * step;
  return Number(snapped.toFixed(6));
}

/** 距离（视口状态栏与"太近了"提示用；NaN 输入按 0 处理而非传染） */
export function distance(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * 点击 vs 拖拽判定（第七轮交互重构）：手动 raycast 挂在 pointerup 上，
 * 而环绕拖拽、gizmo 拖拽松手同样触发 pointerup——不区分就会"转个视角误选一个对象"。
 * 规则：位移 ≤ 阈值且时长 ≤ 阈值才算点击；非有限输入一律判 false（宁可不选不可误选）。
 */
export const CLICK_MAX_DISTANCE_PX = 5;
export const CLICK_MAX_DURATION_MS = 500;
export function isClickLike(
  down: { x: number; y: number; t: number },
  up: { x: number; y: number; t: number },
): boolean {
  const dx = up.x - down.x;
  const dy = up.y - down.y;
  const dt = up.t - down.t;
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(dt)) {
    return false;
  }
  return Math.hypot(dx, dy) <= CLICK_MAX_DISTANCE_PX && dt >= 0 && dt <= CLICK_MAX_DURATION_MS;
}
